import React, { useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, FormFeedback, Form } from 'reactstrap'
import { gql, useMutation } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { AdjusterSchema } from './IncidentModalsSchema'


const CREATE_ADJUSTER = gql`
  mutation CreateAdjuster($input: CreateTPAAdjusterMutationInput!) {
    createTpaAdjuster(input: $input) {
      adjuster {
        id
        name
      }
      errors {
        field
        messages
      }
    }
  }
`

const AddAdjusterModal = ({ isOpen, toggle, tpaId, onCreate }) => {
    const [createAdjuster, { loading }] = useMutation(CREATE_ADJUSTER)
    const [mutationError, setMutationError] = useState(null)
    const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        resolver: yupResolver(AdjusterSchema),
        defaultValues: {
            name: '',
            phone: '',
            email: '',
            role: '',
        }
    })

    const handleCreate = async (formData) => {
        setMutationError(null)
        const input = { ...formData, tpaId }
        try {
            const { data } = await createAdjuster({ variables: { input } })
            if (data?.createTpaAdjuster?.adjuster) {
                onCreate && onCreate(data.createTpaAdjuster.adjuster)
                reset()
                toggle()
            } else if (data?.createTpaAdjuster?.errors?.length > 0) {
                setMutationError(data.createTpaAdjuster.errors[0].messages[0])
            }
        } catch (e) {
            setMutationError(e.message)
        }
    }

    const handleClose = () => {
        reset()
        setMutationError(null)
        toggle()
    }

    return (
        <Modal isOpen={isOpen} toggle={handleClose}>
            <ModalHeader>Create Adjuster</ModalHeader>
            <ModalBody>
                <Form id="add-adjuster-form" onSubmit={handleSubmit(handleCreate)}>
                    <FormGroup>
                        <Label for="name">Name*</Label>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="name"
                                    {...field}
                                    placeholder="Enter adjuster name"
                                    invalid={!!errors.name}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.name?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="adjusterPhone">Phone</Label>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="adjusterPhone"
                                    {...field}
                                    placeholder="Enter adjuster phone"
                                    invalid={!!errors.phone}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.phone?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="adjusterEmail">Email</Label>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="adjusterEmail"
                                    type="email"
                                    {...field}
                                    placeholder="Enter adjuster email"
                                    invalid={!!errors.email}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.email?.message}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="adjusterRole">Role</Label>
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="adjusterRole"
                                    {...field}
                                    placeholder="Enter adjuster role"
                                    invalid={!!errors.role}
                                />
                            )}
                        />
                        <FormFeedback className="d-block">{errors.role?.message}</FormFeedback>
                    </FormGroup>
                    {mutationError && <div className="text-danger small mb-2">{mutationError}</div>}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={handleClose} disabled={loading || isSubmitting}>Cancel</Button>
                <Button color="primary" type="submit" form="add-adjuster-form" disabled={loading || isSubmitting}>
                    {loading ? 'Creating...' : 'Create'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default AddAdjusterModal
